import React, { useState } from 'react';
import { PLANS } from '../lib/pricingConfig';

const KYF_GRID_CSS = `
  .axigon-kyf-grid   { grid-template-columns: repeat(3, 1fr); }
  .axigon-kyf-2col   { grid-template-columns: repeat(2, 1fr); }
  .axigon-kyf-4col   { grid-template-columns: repeat(4, 1fr); }
  @media (max-width: 768px) {
    .axigon-kyf-grid  { grid-template-columns: 1fr !important; }
    .axigon-kyf-2col  { grid-template-columns: 1fr !important; }
    .axigon-kyf-4col  { grid-template-columns: 1fr !important; }
    .axigon-hero-row  { flex-direction: column; }
  }
  @media (max-width: 1000px) and (min-width: 769px) {
    .axigon-kyf-grid  { grid-template-columns: repeat(2, 1fr) !important; }
    .axigon-kyf-2col  { grid-template-columns: 1fr !important; }
    .axigon-kyf-4col  { grid-template-columns: repeat(2, 1fr) !important; }
  }
`;


/*
  LandingPage - company/home page extracted from App.js.
  Props: user, isLoggedIn, navigateTo, setUser, setIsLoggedIn
*/
const LandingPage = ({ user, isLoggedIn, navigateTo, setUser, setIsLoggedIn }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accountType, setAccountType] = useState('personal');
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);


  const handleInlineSignup = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!name.trim() || !email.trim() || !password) {
      setFormError('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }
    setFormLoading(true);
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim(), email: email.trim(), password, accountType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signup failed');
      setUser(data.user);
      setIsLoggedIn(true);
      navigateTo('welcome');
    } catch (err) {
      setFormError(err.message || 'Signup failed');
    } finally {
      setFormLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '9px 14px', borderRadius: '8px',
    border: '1px solid var(--border)', backgroundColor: 'var(--bg)', color: 'var(--text-primary)',
    fontSize: '14px', outline: 'none', transition: 'border-color 0.2s',
    boxSizing: 'border-box', fontFamily: 'inherit',
  };

  return (
        <><style>{KYF_GRID_CSS}</style>

          {/* Hero */}
          <section style={{
            backgroundColor: '#07090D',
            // Finance-themed hero image with a dark overlay so text stays legible.
            backgroundImage: "linear-gradient(180deg, rgba(7,9,13,0.88) 0%, rgba(7,9,13,0.7) 45%, rgba(7,9,13,0.52) 100%), url('/hero-bg.webp')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
            overflow: 'hidden',
            padding: '60px 24px 20px',
          }}>
            {/* Ambient glows */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
              <div style={{ position: 'absolute', top: '-15%', left: '-8%', width: '60%', height: '80%', background: 'radial-gradient(ellipse, rgba(0,194,124,0.09) 0%, transparent 68%)' }} />
              <div style={{ position: 'absolute', bottom: '-20%', right: '-8%', width: '55%', height: '75%', background: 'radial-gradient(ellipse, rgba(37,99,235,0.07) 0%, transparent 68%)' }} />
            </div>

            {isLoggedIn ? (
              <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: '680px', margin: '0 auto' }}>
                <h1 style={{ fontSize: 'clamp(34px, 5vw, 54px)', fontWeight: 700, lineHeight: 1.1, color: '#FFFFFF', marginBottom: '20px', letterSpacing: '-0.03em', textShadow: '0 2px 22px rgba(0,0,0,0.7), 0 1px 3px rgba(0,0,0,0.55)' }}>
                  Personal finance intelligence,<br />
                  <span style={{ color: '#00C27C' }}>built for India.</span>
                </h1>
                <p style={{ fontSize: '17px', color: 'rgba(255,255,255,0.72)', marginBottom: '44px', lineHeight: 1.65, textShadow: '0 1px 12px rgba(0,0,0,0.8)' }}>
                  Finance that finally makes sense.
                </p>
                <button
                  onClick={() => navigateTo(user?.accountType === 'personal' ? 'personal-dashboard' : 'dashboard')}
                  style={{ display: 'inline-block', backgroundColor: '#00C27C', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '14px 32px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', letterSpacing: '-0.01em', transition: 'opacity 0.18s', fontFamily: 'inherit' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >Go to dashboard</button>
              </div>
            ) : (
              <div className="axigon-hero-row" style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '1080px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: 'clamp(32px, 6vw, 80px)' }}>

                {/* Left column */}
                <div style={{ flex: '1.25', minWidth: '300px' }}>

                  {/* Badge */}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(6,18,13,0.6)', border: '1px solid rgba(0,194,124,0.3)', borderRadius: '20px', padding: '5px 14px', marginBottom: '12px', backdropFilter: 'blur(3px)' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#00C27C', display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#00C27C', letterSpacing: '0.04em' }}>Built for Indian professionals</span>
                  </div>

                  <h1 style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 700, lineHeight: 1.1, color: '#FFFFFF', marginBottom: '10px', letterSpacing: '-0.03em', textShadow: '0 2px 22px rgba(0,0,0,0.7), 0 1px 3px rgba(0,0,0,0.55)' }}>
                    Personal finance intelligence,<br />
                    <span style={{ color: '#00C27C' }}>built for India.</span>
                  </h1>

                  <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.72)', lineHeight: 1.6, margin: '0 0 16px', letterSpacing: '-0.005em', maxWidth: '460px', textShadow: '0 1px 12px rgba(0,0,0,0.8)' }}>
                    Finance that finally makes sense.
                  </p>

                  {/* Output preview chips */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {[
                      { label: 'Cash-Flow', value: 'Rs.34,200 is truly free to spend this month', accent: '#00C27C' },
                      { label: 'Debt Analysis', value: 'Prepaying saves Rs.4.2L over 5 years', accent: '#3B82F6' },
                    ].map(({ label, value, accent }) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '14px', backgroundColor: 'rgba(7,9,13,0.55)', border: '1px solid rgba(255,255,255,0.1)', borderLeft: `2px solid ${accent}`, borderRadius: '8px', padding: '7px 12px', backdropFilter: 'blur(3px)' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0, minWidth: '90px' }}>{label}</span>
                        <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.82)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.4 }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right column - signup form */}
                <div id="hero-signup-form" style={{ flex: 1, minWidth: '300px', maxWidth: '400px' }}>
                  <form onSubmit={handleInlineSignup} style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '9px', boxShadow: '0 28px 72px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06)' }}>

                    <div style={{ marginBottom: '2px' }}>
                      <p style={{ fontSize: '16px', fontWeight: 700, color: '#0A0A0C', margin: '0 0 3px', letterSpacing: '-0.02em' }}>Start for free</p>
                      <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>No credit card required</p>
                    </div>

                    <input
                      type="text" placeholder="Your name" value={name}
                      onChange={e => setName(e.target.value)}
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border)'}
                    />
                    <input
                      type="email" placeholder="your@email.com" value={email}
                      onChange={e => setEmail(e.target.value)}
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border)'}
                    />
                    <input
                      type="password" placeholder="Password (min 6 characters)" value={password}
                      onChange={e => setPassword(e.target.value)}
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border)'}
                    />

                    <div style={{ display: 'flex', gap: '8px' }}>
                      {['personal', 'business'].map(type => (
                        <button
                          key={type} type="button"
                          onClick={() => setAccountType(type)}
                          style={{
                            flex: 1, padding: '9px 0', borderRadius: '7px', fontSize: '13px', fontWeight: 500,
                            cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                            backgroundColor: accountType === type ? 'var(--accent)' : 'transparent',
                            border: `1px solid ${accountType === type ? 'var(--accent)' : 'var(--border)'}`,
                            color: accountType === type ? '#ffffff' : 'var(--text-secondary)',
                          }}
                        >{type.charAt(0).toUpperCase() + type.slice(1)}</button>
                      ))}
                    </div>

                    <button
                      type="submit" disabled={formLoading}
                      style={{ width: '100%', backgroundColor: 'var(--accent)', color: '#ffffff', border: 'none', borderRadius: '8px', height: '42px', fontSize: '14px', fontWeight: 600, cursor: formLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'opacity 0.18s', opacity: formLoading ? 0.7 : 1 }}
                      onMouseEnter={e => { if (!formLoading) e.currentTarget.style.opacity = '0.87'; }}
                      onMouseLeave={e => { if (!formLoading) e.currentTarget.style.opacity = '1'; }}
                    >{formLoading ? 'Creating account...' : 'Create free account'}</button>

                    {formError && (
                      <p style={{ fontSize: '13px', color: '#EF4444', margin: 0, lineHeight: 1.5 }}>{formError}</p>
                    )}

                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, textAlign: 'center' }}>
                      Already have an account?{' '}
                      <button
                        type="button" onClick={() => navigateTo('login')}
                        style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: '12px', cursor: 'pointer', padding: 0, fontFamily: 'inherit', textDecoration: 'underline', transition: 'opacity 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                      >Log in</button>
                    </p>
                  </form>
                </div>

              </div>
            )}
          </section>

          {/* Know Your Finances */}
          <section style={{ backgroundColor: 'var(--bg-page)', borderTop: '1px solid var(--border)', padding: '24px 24px 64px' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>

              {/* Heading */}
              <div style={{ marginBottom: '14px' }}>
                <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 8px' }}>Know Your Finances</p>
                <h2 style={{ fontSize: 'clamp(22px, 2.6vw, 32px)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.25, margin: 0, letterSpacing: '-0.02em' }}>
                  Finance that finally makes sense.
                </h2>
              </div>

              {/* Feature tiles - all 4 in one row */}
              <div className="axigon-kyf-4col" style={{ display: 'grid', gap: '14px', alignItems: 'stretch' }}>

                {/* Tile 2 - Transform Financial Docs */}
                <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-card)' }}>
                  <div style={{ height: '128px', background: 'linear-gradient(135deg, rgba(16,185,129,0.14), rgba(16,185,129,0.03))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="84" height="84" viewBox="0 0 96 96" fill="none">
                      <rect x="24" y="16" width="40" height="54" rx="7" fill="#10B981" opacity="0.18" />
                      <rect x="33" y="23" width="40" height="54" rx="7" fill="#10B981" opacity="0.32" />
                      <rect x="40" y="31" width="22" height="4" rx="2" fill="#10B981" opacity="0.65" />
                      <rect x="40" y="39" width="15" height="4" rx="2" fill="#10B981" opacity="0.45" />
                      <rect x="41" y="60" width="6" height="10" rx="2" fill="#10B981" />
                      <rect x="50" y="54" width="6" height="16" rx="2" fill="#10B981" />
                      <rect x="59" y="48" width="6" height="22" rx="2" fill="#10B981" />
                    </svg>
                  </div>
                  <div style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', flex: 1, gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', letterSpacing: '-0.01em' }}>Transform Financial Docs</p>
                      <p style={{ fontSize: '12.5px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>Turn a statement into your full financial picture.</p>
                    </div>
                    <button onClick={() => navigateTo(isLoggedIn ? 'life-finance' : 'signup')} style={{ width: '100%', backgroundColor: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 0', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.2s' }} onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--text-tertiary)'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>Upload a document →</button>
                  </div>
                </div>

                {/* Tile 3 - Debt / EMI Intelligence */}
                <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-card)' }}>
                  <div style={{ height: '128px', background: 'linear-gradient(135deg, rgba(59,130,246,0.14), rgba(59,130,246,0.03))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="84" height="84" viewBox="0 0 96 96" fill="none">
                      <circle cx="48" cy="18" r="4.5" fill="#3B82F6" />
                      <rect x="45.5" y="20" width="5" height="48" rx="2.5" fill="#3B82F6" opacity="0.55" />
                      <rect x="33" y="66" width="30" height="6" rx="3" fill="#3B82F6" />
                      <rect x="27" y="27" width="42" height="5" rx="2.5" fill="#3B82F6" />
                      <path d="M30 31 L21 45 M30 31 L39 45" stroke="#3B82F6" strokeWidth="2" opacity="0.5" strokeLinecap="round" />
                      <path d="M19 44 a11 11 0 0 0 22 0 Z" fill="#3B82F6" opacity="0.3" />
                      <path d="M66 31 L57 45 M66 31 L75 45" stroke="#3B82F6" strokeWidth="2" opacity="0.5" strokeLinecap="round" />
                      <path d="M55 44 a11 11 0 0 0 22 0 Z" fill="#3B82F6" opacity="0.3" />
                    </svg>
                  </div>
                  <div style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', flex: 1, gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', letterSpacing: '-0.01em' }}>Debt / EMI Intelligence</p>
                      <p style={{ fontSize: '12.5px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>Prepay your loan or invest? Get the real math.</p>
                    </div>
                    <button onClick={() => navigateTo('debt-intelligence')} style={{ width: '100%', backgroundColor: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 0', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.2s' }} onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--text-tertiary)'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>Calculate →</button>
                  </div>
                </div>

                {/* Tile 4 - Can I Afford This? Decision Engine */}
                <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-card)' }}>
                  <div style={{ height: '128px', background: 'linear-gradient(135deg, rgba(139,92,246,0.14), rgba(139,92,246,0.03))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="84" height="84" viewBox="0 0 96 96" fill="none">
                      <path d="M22 64 a26 26 0 1 1 52 0" stroke="#8B5CF6" strokeWidth="7" opacity="0.2" strokeLinecap="round" />
                      <path d="M22 64 a26 26 0 0 1 41 -21" stroke="#8B5CF6" strokeWidth="7" strokeLinecap="round" />
                      <path d="M48 64 L61 46" stroke="#8B5CF6" strokeWidth="4.5" strokeLinecap="round" />
                      <circle cx="48" cy="64" r="6.5" fill="#8B5CF6" />
                    </svg>
                  </div>
                  <div style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', flex: 1, gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', letterSpacing: '-0.01em' }}>Decision Engine</p>
                      <p style={{ fontSize: '12.5px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>Can I afford this? A real yes / no answer.</p>
                    </div>
                    <button onClick={() => navigateTo('affordability-engine')} style={{ width: '100%', backgroundColor: '#8B5CF6', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 0', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'opacity 0.2s' }} onMouseEnter={e => e.currentTarget.style.opacity = '0.85'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>Can I afford it? →</button>
                  </div>
                </div>

                {/* Tile 5 - Cash-Flow Reality */}
                <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-card)' }}>
                  <div style={{ height: '128px', background: 'linear-gradient(135deg, rgba(245,158,11,0.14), rgba(245,158,11,0.03))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="84" height="84" viewBox="0 0 96 96" fill="none">
                      <ellipse cx="48" cy="64" rx="17" ry="6.5" fill="#F59E0B" opacity="0.3" />
                      <ellipse cx="48" cy="56" rx="17" ry="6.5" fill="#F59E0B" opacity="0.55" />
                      <ellipse cx="48" cy="48" rx="17" ry="6.5" fill="#F59E0B" />
                      <path d="M33 33 a16 11 0 0 1 30 -2" stroke="#F59E0B" strokeWidth="3.5" strokeLinecap="round" fill="none" />
                      <path d="M63 31 l-1 -7 M63 31 l6 2" stroke="#F59E0B" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </svg>
                  </div>
                  <div style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', flex: 1, gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', letterSpacing: '-0.01em' }}>Cash-Flow Reality</p>
                      <p style={{ fontSize: '12.5px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>See your true discretionary income, not just your balance.</p>
                    </div>
                    <button onClick={() => navigateTo('cashflow-reality')} style={{ width: '100%', backgroundColor: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 0', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.2s' }} onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--text-tertiary)'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>See the reality →</button>
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* See it in action */}
          <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 24px 96px' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>

              {/* Heading */}
              <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                <h2 style={{ fontSize: 'clamp(26px, 3.5vw, 40px)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2, margin: 0, letterSpacing: '-0.025em' }}>
                  Built for real problems, not demos.
                </h2>
              </div>

              {/* Cards row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px', maxWidth: '520px', margin: '0 auto' }}>

                {/* ── Ask Axigon Finance card ── */}
                <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
                  {/* Card header */}
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#EF4444' }} />
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#F59E0B' }} />
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10B981' }} />
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginLeft: '4px', fontWeight: 500 }}>Ask Axigon Finance</span>
                  </div>
                  {/* Card body */}
                  <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: '0' }}>
                    {/* Summary label */}
                    <p style={{ fontSize: '10px', fontWeight: 700, color: '#10B981', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 18px' }}>Financial Summary</p>
                    {/* Stat rows */}
                    {[
                      { label: 'Monthly Income',   value: 'Rs.85,000' },
                      { label: 'Monthly Expenses', value: 'Rs.38,400' },
                      { label: 'Current Balance',  value: 'Rs.2,41,500' },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '11px 0', borderBottom: '1px solid var(--border)' }}>
                        <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
                        <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>{value}</span>
                      </div>
                    ))}
                    {/* AI insight chip */}
                    <div style={{ marginTop: '18px', padding: '12px 14px', backgroundColor: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: '8px' }}>
                      <p style={{ fontSize: '12px', color: '#10B981', lineHeight: 1.6, margin: 0 }}>
                        You save 55% of your income. At this rate, you can build a 6-month emergency fund in 4 months.
                      </p>
                    </div>
                  </div>
                  {/* Caption */}
                  <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.55 }}>Upload a bank statement. Get a full financial summary in seconds.</p>
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* Pricing section - data from shared pricingConfig */}
          <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 24px 112px' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>

              <div style={{ marginBottom: '56px' }}>
                <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 16px' }}>Pricing</p>
                <h2 style={{ fontSize: 'clamp(24px, 3.2vw, 36px)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2, margin: 0, letterSpacing: '-0.02em' }}>
                  Simple, honest pricing.
                </h2>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', alignItems: 'start' }}>
                {PLANS.map((plan) => {
                  const { id, name, price, period, desc, features, highlight, badge } = plan;
                  const cta = id === 'expert' ? 'Connect now' : (isLoggedIn ? 'Go to dashboard' : plan.cta);
                  const onCta = id === 'expert'
                    ? () => navigateTo('professionals-directory')
                    : () => navigateTo(isLoggedIn ? (user?.accountType === 'personal' ? 'personal-dashboard' : 'dashboard') : 'signup');
                  return (
                    <div key={id} style={{
                      backgroundColor: 'var(--bg)',
                      border: highlight ? '2px solid var(--accent)' : '1px solid var(--border)',
                      borderRadius: '12px',
                      padding: '32px 28px',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                    }}>
                      {badge && (
                        <span style={{ position: 'absolute', top: '-1px', right: '24px', backgroundColor: 'var(--accent)', color: '#ffffff', fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '0 0 6px 6px' }}>{badge}</span>
                      )}
                      <p style={{ fontSize: '12px', fontWeight: 700, color: highlight ? 'var(--accent)' : 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 16px' }}>{name}</p>
                      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                        <span style={{ fontSize: '32px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>{price}</span>
                        {period && <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{period}</span>}
                      </div>
                      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 28px' }}>{desc}</p>
                      <div style={{ borderTop: '1px solid var(--border)', paddingTop: '24px', marginBottom: '28px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {features.map(f => (
                          <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <span style={{ color: highlight ? 'var(--accent)' : 'var(--text-tertiary)', fontSize: '14px', lineHeight: 1, marginTop: '2px', flexShrink: 0 }}>-</span>
                            <span style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{f}</span>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={onCta}
                        style={{
                          marginTop: 'auto',
                          padding: '11px 20px',
                          borderRadius: '7px',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          transition: 'background-color 0.2s, border-color 0.2s',
                          ...(highlight
                            ? { backgroundColor: 'var(--accent)', color: '#ffffff', border: '1px solid var(--accent)' }
                            : { backgroundColor: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border)' }
                          ),
                        }}
                        onMouseEnter={e => {
                          if (highlight) { e.currentTarget.style.backgroundColor = 'var(--accent-hover)'; }
                          else { e.currentTarget.style.borderColor = 'var(--text-tertiary)'; }
                        }}
                        onMouseLeave={e => {
                          if (highlight) { e.currentTarget.style.backgroundColor = 'var(--accent)'; }
                          else { e.currentTarget.style.borderColor = 'var(--border)'; }
                        }}
                      >{cta}</button>
                    </div>
                  );
                })}
              </div>

            </div>
          </section>

          {/* Trust signals */}
          <section style={{ backgroundColor: 'var(--bg-page)', borderTop: '1px solid var(--border)', padding: '32px 24px' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'center', gap: '40px', flexWrap: 'wrap' }}>
              {[
                { glyph: '\u2014', text: 'Your data is encrypted at rest and in transit' },
                { glyph: '\u2014', text: 'We never sell your data to third parties' },
                { glyph: '\u2014', text: 'Built for Indian financial and legal context' },
                { glyph: '\u21BA', text: 'Cancel anytime, no questions asked' },
              ].map(({ glyph, text }) => (
                <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-tertiary)', lineHeight: 1, fontStyle: 'normal' }}>{glyph}</span>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}>{text}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Footer */}
          <footer style={{ backgroundColor: 'var(--bg-page)', borderTop: '1px solid var(--border)', padding: '36px 24px' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
                <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>AxigonAI</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '28px' }}>
                  <button onClick={() => navigateTo('about')}
                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer', padding: 0, fontFamily: 'inherit', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}>About Us</button>
                  <button onClick={() => navigateTo('privacy')}
                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer', padding: 0, fontFamily: 'inherit', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}>Privacy Policy</button>
                  <a href="mailto:hello@axigonai.com"
                    style={{ color: 'var(--text-secondary)', fontSize: '13px', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}>Contact</a>
                </div>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', margin: 0 }}>© 2025 Axigon AI. All rights reserved.</p>
            </div>
          </footer>
        </>
  );
};

export default LandingPage;
